import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { GAME_CONSTANTS } from '@/types/game';

interface StarfieldProps {
  playerPosition: THREE.Vector3;
  velocity: number;
}

export const Starfield = ({ playerPosition, velocity }: StarfieldProps) => {
  const nearStarsRef = useRef<THREE.Points>(null);
  const farStarsRef = useRef<THREE.Points>(null);

  // Generate star positions
  const { nearPositions, farPositions, nearColors, farColors } = useMemo(() => {
    const nearCount = 2000;
    const farCount = 5000;
    const nearPositions = new Float32Array(nearCount * 3);
    const farPositions = new Float32Array(farCount * 3);
    const nearColors = new Float32Array(nearCount * 3);
    const farColors = new Float32Array(farCount * 3);

    const worldSize = GAME_CONSTANTS.WORLD_SIZE * 2;

    // Near stars - smaller spread, move fast
    for (let i = 0; i < nearCount; i++) {
      nearPositions[i * 3] = (Math.random() - 0.5) * worldSize;
      nearPositions[i * 3 + 1] = (Math.random() - 0.5) * worldSize;
      nearPositions[i * 3 + 2] = (Math.random() - 0.5) * worldSize;

      // Cyan to white colors
      const brightness = 0.5 + Math.random() * 0.5;
      nearColors[i * 3] = brightness * 0.7;
      nearColors[i * 3 + 1] = brightness;
      nearColors[i * 3 + 2] = brightness;
    }

    // Far stars - bigger spread, move slow
    for (let i = 0; i < farCount; i++) {
      farPositions[i * 3] = (Math.random() - 0.5) * worldSize * 3;
      farPositions[i * 3 + 1] = (Math.random() - 0.5) * worldSize * 3;
      farPositions[i * 3 + 2] = (Math.random() - 0.5) * worldSize * 3;

      // Dim blue/purple colors
      const brightness = 0.2 + Math.random() * 0.3;
      farColors[i * 3] = brightness * 0.8;
      farColors[i * 3 + 1] = brightness * 0.8;
      farColors[i * 3 + 2] = brightness;
    }

    return { nearPositions, farPositions, nearColors, farColors };
  }, []);

  // Parallax effect
  useFrame(() => {
    if (nearStarsRef.current && farStarsRef.current) {
      // Near stars follow player closely (fast parallax)
      nearStarsRef.current.position.copy(playerPosition).multiplyScalar(0.3);
      
      // Far stars follow player slowly (slow parallax)
      farStarsRef.current.position.copy(playerPosition).multiplyScalar(0.05);
    }
  });

  return (
    <>
      {/* Near stars - fast moving */}
      <points ref={nearStarsRef}>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={nearPositions.length / 3}
            array={nearPositions}
            itemSize={3}
          />
          <bufferAttribute
            attach="attributes-color"
            count={nearColors.length / 3}
            array={nearColors}
            itemSize={3}
          />
        </bufferGeometry>
        <pointsMaterial
          size={0.8}
          vertexColors
          transparent
          opacity={0.8}
          sizeAttenuation
        />
      </points>

      {/* Far stars - slow moving */}
      <points ref={farStarsRef}>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={farPositions.length / 3}
            array={farPositions}
            itemSize={3}
          />
          <bufferAttribute
            attach="attributes-color"
            count={farColors.length / 3}
            array={farColors}
            itemSize={3}
          />
        </bufferGeometry>
        <pointsMaterial
          size={0.4}
          vertexColors
          transparent
          opacity={0.5}
          sizeAttenuation
        />
      </points>
    </>
  );
};
